import React, { useEffect, useState } from "react";
import type { File } from "react-pdf/dist/cjs/shared/types";

// Which HTTP header the fetched PDF endpoint expects the auth token on.
// - "bearer"    → `Authorization: Bearer <token>` — used by direct upstream calls (e.g. UPS
//                 receipt endpoint on UPS API Gateway).
// - "cts-token" → `X-CTS-Token: <token>` — used by the postbill PDF proxy on UPP Server
//                 (PostbillProxyHandler), which itself translates to `Authorization: Bearer`
//                 before forwarding to UPP Billing.
export type PdfAuthScheme = "bearer" | "cts-token";

type UseBillingPdfFileProps = {
  url: string;
  token: string | null;
  authScheme?: PdfAuthScheme;
};

type UseBillingPdfFileController = {
  file: File | null;
  pdfError: boolean;
};

const useBillingPdfFile = ({
  url,
  token,
  authScheme = "bearer",
}: UseBillingPdfFileProps): UseBillingPdfFileController => {
  const [pdfError, setPdfError] = useState<boolean>(false);

  useEffect(() => {
    if (!token) setPdfError(true);
  }, [url]);

  const file = React.useMemo(() => {
    if (!url) {
      return null;
    }
    if (!token) {
      return null;
    }
    const httpHeaders =
      authScheme === "cts-token"
        ? {
            "X-CTS-Token": token,
            Accept: "application/pdf",
          }
        : {
            Authorization: `Bearer ${token}`,
            Accept: "application/pdf",
          };
    return {
      url,
      httpHeaders,
    };
  }, [url, token, authScheme]);

  return { file, pdfError };
};

export default useBillingPdfFile;
